import React, { useCallback, useEffect, useState } from 'react';
import { agentApi } from '../../services/api';
import type { ConversationSummary } from './types';

// PHA-1155: the clinician's own past Ask Phanova conversations (last 90 days,
// current organization). Picking one reopens it and continues the same thread.

function relativeTime(iso?: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export interface ThreadListProps {
  activeKey?: string | null;
  onSelect: (conversationKey: string) => void;
  onNewChat: () => void;
  // Bumped by the parent after a turn completes so the list picks up the
  // thread's new position and title.
  refreshToken?: number;
}

const ThreadList: React.FC<ThreadListProps> = ({ activeKey, onSelect, onNewChat, refreshToken }) => {
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await agentApi.listConversations(40);
      setItems(res.conversations || []);
      setError(false);
    } catch {
      setError(true);
      setItems((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  return (
    <nav
      aria-label="Past conversations"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <div style={{ padding: '12px 12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          History
        </span>
        <button
          type="button"
          onClick={onNewChat}
          style={{
            fontSize: 12,
            padding: '4px 10px',
            borderRadius: 6,
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#334155',
            cursor: 'pointer',
          }}
        >
          + New chat
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 6px 12px' }}>
        {items === null && <div style={{ padding: 12, fontSize: 13, color: '#94a3b8' }}>Loading…</div>}
        {error && (
          <div style={{ padding: 12, fontSize: 13, color: '#b45309' }}>
            Couldn't load history.{' '}
            <button type="button" onClick={load} style={{ all: 'unset', color: '#1d4ed8', cursor: 'pointer' }}>
              Retry
            </button>
          </div>
        )}
        {items && items.length === 0 && !error && (
          <div style={{ padding: 12, fontSize: 13, color: '#94a3b8' }}>No past conversations yet.</div>
        )}
        {items?.map((c) => {
          const active = c.conversationKey === activeKey;
          return (
            <button
              key={c.conversationKey}
              type="button"
              onClick={() => onSelect(c.conversationKey)}
              aria-current={active ? 'true' : undefined}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                margin: '1px 0',
                borderRadius: 8,
                border: 'none',
                background: active ? '#eff6ff' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: active ? '#1d4ed8' : '#0f172a',
                  fontWeight: active ? 600 : 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={c.title}
              >
                {c.title}
              </div>
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                {relativeTime(c.lastActivityAt)}
                {c.messageCount ? ` · ${c.messageCount} messages` : ''}
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default ThreadList;
